import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, of, Subscription, switchMap } from 'rxjs';

import { CentralService } from '../../central.service';
import { ConfigService } from '../../config.service';
import { CoreApiService, WriteResult } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { AvatarDialogComponent } from '../../profile/avatar-dialog.component';
import { ProfileService } from '../../profile/profile.service';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { Profile } from '../../shared/models/profile.model';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { ConfirmDialog, DialogService } from '..';

@Component({
  host: {
    'test-id': 'profile-edit-dialog',
  },
  selector: 'profile-edit-dialog',
  templateUrl: './profile-edit-dialog.component.html',
  styleUrls: ['./profile-edit-dialog.component.scss'],
})
export class ProfileEditDialogComponent implements OnInit {
  profile: Profile;
  messages = {
    common: TranslationBundle.EMPTY,
    profile: TranslationBundle.EMPTY,
  };
  userInfo = new FormGroup({
    firstname: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(32)]),
    lastname: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(32)]),
    email: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    ldap_sync_id: new FormControl(''),
    role: new FormControl(''),
  });

  SiteRole = SiteRole; // expose to template

  private subscriptions = {
    messages: Subscription.EMPTY,
    saveUserInfo: Subscription.EMPTY,
    central: Subscription.EMPTY,
  };

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private notifications: NotificationService,
    private dialogRef: DialogRef<ProfileEditDialogComponent>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private translate: TranslationService,
    private profileService: ProfileService,
    private centralService: CentralService,
    @Inject(DIALOG_DATA) public data?: { profile: Profile, manageOtherUser?: boolean },
  ) {
    this.profile = data.profile;
    this.userInfo.patchValue(this.profile);

    // once set ldap id cannot be changed
    const userAlreadyHasLdapId = Boolean(this.profile.ldap_sync_id);
    if (userAlreadyHasLdapId) {
      this.userInfo.controls.ldap_sync_id.disable();
    }

    if (config.getCentralConfig().enabled) {
      this.userInfo.disable();
      this.subscriptions.central = combineLatest([
        this.centralService.getCurrentOrg(),
        this.centralService.getUserManagementUrl(),
      ]).subscribe({
        next: ([org, url]) => {
          this.centralOrg = org.name;
          this.centralManagementLink = url;
        },
        error: (error) => {
          this.logger.error(error);
        },
      });
    }
    if (data.manageOtherUser) {
      this.userInfo.get('role').setValue(SiteRole.getPrimaryRole(this.profile));
    }
  }

  centralEnabled = this.config.getCentralConfig().enabled;
  centralManagementLink = '';
  centralOrg = '';

  ngOnInit(): void {
    this.subscriptions.messages = combineLatest([
      this.translate.getBundle('common'),
      this.translate.getBundle('profile'),
    ]).subscribe(([common, profile]) => {
      this.messages = { common, profile };
    });
  }

  getErrorMessage(controlName: string): Observable<string> {
    if (controlName === 'firstname' && this.userInfo.controls[controlName].errors?.required) {
      return this.translate.getMessage('common.errors.firstNameRequired');
    } else if (controlName === 'lastname' && this.userInfo.controls[controlName].errors?.required) {
      return this.translate.getMessage('common.errors.lastNameRequired');
    } else if (controlName === 'email' && this.userInfo.controls[controlName].errors?.required) {
      return this.translate.getMessage('common.errors.emailRequired');
    } else if (this.userInfo.controls[controlName].errors?.minlength) {
      return this.translate.getMessage('common.errors.nameShort');
    } else if (this.userInfo.controls[controlName].errors?.maxlength) {
      return this.translate.getMessage('common.errors.nameLong');
    } else if (this.userInfo.controls[controlName].errors?.email) {
      return this.translate.getMessage('common.errors.emailInvalid');
    } else {
      return of('');
    }
  }

  editAvatar(): void {
    this.close();
    this.dialog.open(AvatarDialogComponent, {
      data: { profile: this.profile },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  saveUserInfo(): void {
    if (this.userInfo.invalid) {
      return;
    }
    this.subscriptions.saveUserInfo.unsubscribe();
    const updatedProfile: Partial<Profile> = {
      firstname: this.userInfo.value.firstname,
      lastname: this.userInfo.value.lastname,
      email: this.userInfo.value.email,
      ldap_sync_id: this.userInfo.value.ldap_sync_id,
    };

    let saveObservable: Observable<Profile|WriteResult>;
    if (this.centralEnabled) {
      return;
    }
    if (this.data.manageOtherUser) {
      updatedProfile.roles = [this.userInfo.get('role').value as SiteRole],
      saveObservable = this.coreApi.users.put(this.profile._id, updatedProfile);
    } else {
      saveObservable = this.profileService.putProfile(updatedProfile);
    }
    this.subscriptions.saveUserInfo = saveObservable.subscribe({
      next: () => {
        this.notifications.success('profile.updateInfo.success');
        this.close();
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('profile.updateInfo.error');
      },
    });
  }

  reactivateUser(): void {
    this.coreApi.users.put(this.profile._id, { disabled: false }).subscribe({
      next: () => {
        this.notifications.success('userManagement.enableUser.success');
        this.close();
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('userManagement.enableUser.error');
      },
    });
  }

  deactivateUser(): void {
    this.dialog.open(ConfirmDialog, {
      data: {
        title: '',
        confirmButtonText: this.messages.common.get('labels.yes'),
        cancelButtonText: this.messages.common.get('labels.no'),
        content: this.messages.profile.get('content.clearPermissions'),
      },
    }).afterClosed().pipe(
      switchMap((result) => {
        return this.coreApi.users.put(this.profile._id, { disabled: true }, (result ? { clear_permissions: true } : {}))
      }),
    ).subscribe({
      next: () => {
        this.notifications.success('userManagement.disableUser.success');
        this.close();
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('userManagement.disableUser.error');
      },
    });
  }

  navigateToCentralManagement(): void {
    window.open(this.centralManagementLink, '_blank');
  }
}
